/**
 * AlertBanner — colored banner with icon for urgent notifications.
 */

import type { AlertBlock } from '../../../stores/CoPilotStore'

const alertStyles = {
  info: {
    bg: 'bg-[#EFF6FF]',
    border: 'border-[#BFDBFE]',
    icon: 'text-[#3B82F6]',
    title: 'text-[#1E40AF]',
    message: 'text-[#1E3A5F]',
  },
  warning: {
    bg: 'bg-[#FFFBEB]',
    border: 'border-[#FDE68A]',
    icon: 'text-[#F59E0B]',
    title: 'text-[#92400E]',
    message: 'text-[#78350F]',
  },
  error: {
    bg: 'bg-[#FEF2F2]',
    border: 'border-[#FECACA]',
    icon: 'text-[#EF4444]',
    title: 'text-[#991B1B]',
    message: 'text-[#7F1D1D]',
  },
  success: {
    bg: 'bg-[#F0FDF4]',
    border: 'border-[#BBF7D0]',
    icon: 'text-[#16A34A]',
    title: 'text-[#166534]',
    message: 'text-[#14532D]',
  },
}

function AlertIcon({ severity, className }: { severity: string; className?: string }) {
  if (severity === 'success') {
    return (
      <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
      </svg>
    )
  }
  if (severity === 'warning') {
    return (
      <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z" />
      </svg>
    )
  }
  if (severity === 'error') {
    return (
      <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 3.75h.008v.008H12v-.008Z" />
      </svg>
    )
  }
  // info
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="m11.25 11.25.041-.02a.75.75 0 0 1 1.063.852l-.708 2.836a.75.75 0 0 0 1.063.853l.041-.021M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9-3.75h.008v.008H12V8.25Z" />
    </svg>
  )
}

export function AlertBanner({ block }: { block: AlertBlock }) {
  const style = alertStyles[block.severity] || alertStyles.info

  return (
    <div className={`${style.bg} border ${style.border} rounded-lg px-4 py-3 flex items-start gap-3`}>
      <AlertIcon severity={block.severity} className={`size-5 shrink-0 mt-0.5 ${style.icon}`} />
      <div className="flex-1 min-w-0">
        <div className={`text-[14px] font-semibold ${style.title}`}>{block.title}</div>
        <div className={`text-[13px] mt-0.5 ${style.message}`}>{block.message}</div>
      </div>
    </div>
  )
}
